import { useCallback, useRef } from 'react'

import { GuideTask } from '@/features/guide/types'
import { track } from '@/shared/analytics/analyticsService'

const TRACKED_TASKS_KEY = 'brandtrack_guide_tracked_tasks'
const PREVIOUS_STATUS_KEY = 'brandtrack_guide_previous_status'

/**
 * Load tracked tasks from localStorage synchronously
 */
const loadTrackedTasks = (): Set<string> => {
	try {
		const stored = localStorage.getItem(TRACKED_TASKS_KEY)
		if (stored) {
			const parsed = JSON.parse(stored) as string[]
			return new Set(parsed)
		}
	} catch {
		// Ignore parse errors
	}
	return new Set()
}

/**
 * Load previous status from localStorage synchronously
 */
const loadPreviousStatus = (): Record<string, boolean> | null => {
	try {
		const stored = localStorage.getItem(PREVIOUS_STATUS_KEY)
		if (stored) {
			return JSON.parse(stored) as Record<string, boolean>
		}
	} catch {
		// Ignore parse errors
	}
	return null
}

/**
 * Save previous status to localStorage
 */
const savePreviousStatus = (status: Record<string, boolean>) => {
	try {
		localStorage.setItem(PREVIOUS_STATUS_KEY, JSON.stringify(status))
	} catch {
		// Ignore storage errors
	}
}

/**
 * Hook for tracking guide task completion events.
 * Ensures each task completion is only tracked once per user.
 *
 * Only fires events when a task transitions from incomplete → complete,
 * not on initial load even if tasks are already complete.
 */
export const useGuideTaskTracking = () => {
	// Load tracked tasks synchronously to avoid race conditions
	const trackedTasksRef = useRef<Set<string>>(loadTrackedTasks())

	// Load previous status from localStorage to persist across hook instances
	const previousStatusRef = useRef<Record<string, boolean> | null>(
		loadPreviousStatus()
	)

	/**
	 * Persist tracked tasks to localStorage
	 */
	const persistTrackedTasks = useCallback(() => {
		try {
			const tasksArray = Array.from(trackedTasksRef.current)
			localStorage.setItem(TRACKED_TASKS_KEY, JSON.stringify(tasksArray))
		} catch {
			// Ignore storage errors
		}
	}, [])

	/**
	 * Check if a task has already been tracked
	 */
	const isTaskTracked = useCallback((taskId: string): boolean => {
		return trackedTasksRef.current.has(taskId)
	}, [])

	/**
	 * Track a single task completion event
	 */
	const trackTaskCompleted = useCallback(
		(task: GuideTask) => {
			// Don't track if already tracked
			if (trackedTasksRef.current.has(task.id)) {
				return
			}

			// Mark as tracked
			trackedTasksRef.current.add(task.id)
			persistTrackedTasks()

			track('guide_task_completed', {
				task_id: task.id,
				task_title: task.title,
				task_completion_key: task.completionKey,
				task_is_required: task.isRequired,
				task_navigate_to: task.navigateTo,
			})
		},
		[persistTrackedTasks]
	)

	/**
	 * Track task completions based on state transitions.
	 * Only fires events when a task transitions from false → true,
	 * not when it's already true on first check.
	 */
	const trackTaskCompletions = useCallback(
		(tasks: GuideTask[], completionStatus: Record<string, boolean>) => {
			const previousStatus = previousStatusRef.current

			// On first call ever (no previous status in localStorage),
			// just store the current status without tracking
			// This prevents tracking tasks that are already complete on page load
			if (previousStatus === null) {
				previousStatusRef.current = { ...completionStatus }
				savePreviousStatus(completionStatus)
				return
			}

			tasks.forEach((task) => {
				const wasCompleted = previousStatus[task.id] ?? false
				const isNowCompleted = completionStatus[task.id] ?? false
				const wasAlreadyTracked = trackedTasksRef.current.has(task.id)

				// Only track if task transitioned from incomplete → complete
				// and hasn't been tracked before
				if (!wasCompleted && isNowCompleted && !wasAlreadyTracked) {
					trackTaskCompleted(task)
				}
			})

			// Update previous status for next comparison (both in ref and localStorage)
			previousStatusRef.current = { ...completionStatus }
			savePreviousStatus(completionStatus)
		},
		[trackTaskCompleted]
	)

	/**
	 * Track when all required tasks are completed (guide finished)
	 */
	const trackGuideCompleted = useCallback(
		(requiredTasksCount: number, completedRequiredCount: number) => {
			const isGuideComplete =
				requiredTasksCount > 0 && completedRequiredCount === requiredTasksCount

			if (isGuideComplete && !trackedTasksRef.current.has('guide_completed')) {
				trackedTasksRef.current.add('guide_completed')
				persistTrackedTasks()

				track('guide_completed', {
					total_required_tasks: requiredTasksCount,
					completed_required_tasks: completedRequiredCount,
				})
			}
		},
		[persistTrackedTasks]
	)

	/**
	 * Track a task completion by task ID.
	 * Use this for direct tracking when an action completes,
	 * instead of relying on reactive state changes.
	 */
	const trackTaskById = useCallback(
		(taskId: string) => {
			// Don't track if already tracked
			if (trackedTasksRef.current.has(taskId)) {
				return
			}

			// Mark as tracked
			trackedTasksRef.current.add(taskId)
			persistTrackedTasks()

			track('guide_task_completed', { task_id: taskId })
		},
		[persistTrackedTasks]
	)

	return {
		trackTaskCompleted,
		trackTaskCompletions,
		trackGuideCompleted,
		trackTaskById,
		isTaskTracked,
	}
}
